import React, { useState, useImperativeHandle, useRef, forwardRef, useEffect, useMemo } from 'react';
import TableForm, { FormColumn } from '#components/TableForm';
import { Button, Table, Modal, Empty, Space } from '@bedrock/components';
import { Delete, Plus, NoticeTextLeft } from '@bedrock/icons-react';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { SFormItemProps } from '#components/SForm/SFormItem';
import NoFiles from '@bedrock/components/es/Empty/images/small/NoFiles'
import { calculateTimeConsuming } from '#features/utils';
import {useDemandOptions} from "@mpc/common/src/features/useDemandOptions";
import moment from 'moment';
import day from 'dayjs';
import _ from 'lodash';
import "./index.less"
import OvertimeTag from '#features/OvertimeTag';
import {ImportFileType, useImportFileModal} from "#features/importFileModal";
import { localPx2rem } from '#utils';


type Props = {
    value?: any[];
    onChange?: (value: any[]) => void;
    className?: string;
    view?: boolean;
}

const Index = (props: Props, ref) => {
    const { className, view = false } = props
    const tableFormRef = useRef(null)
    const [state, setOrgState] = useControllableValue(props, {
        defaultValue: []
    })

    const setState = useMemoizedFn((value = []) => {
        // 解决删除后，数据不更新问题
        const deleteList = state?.filter(n => n.delete) || []
        const newState = [...value, ...deleteList]
        setOrgState(newState)
    })

    const showValue = useMemo(() => {
        return state?.filter(n => !n.delete)
    }, [state])

    const handleDelete = useMemoizedFn((record) => {
        const newValue = [...state]
        if (record?.id) {
            const index = state.findIndex(n => n.id === record?.id)
            newValue[index].delete = true
        } else {
            const index = state.findIndex(n => n.key === record?.key)
            newValue.splice(index, 1)
        }
        setOrgState(newValue)
    });

    useImperativeHandle(ref, () => {
        return {
            validateFields: () => {
                return tableFormRef.current?.form?.validateFields()
                    .catch((error) => {
                        const errMessage = error?.errorFields?.[0]?.errors?.[0]
                        return Promise.reject(errMessage)
                    })
            },
            form: tableFormRef.current?.form
        }
    }, [])


    const disabledTime = (current, record, startTime = '') => {
        const timer = day().endOf('day')
        const hour = day(record?.key).hour()
        const minute = day(record?.key).minute()
        const startTimeHour = day(startTime).hour()
        const startTimeMinute = day(startTime).minute()
        if (day(current).endOf('day') < timer) {
            return {}
        }
        return {
            disabledHours: () => {
                const hours = [];
                for (let i = 0; i < 24; i++) {
                    if (startTime && startTimeHour) {
                        if (i < startTimeHour || i > hour) {
                            hours.push(i);
                        }
                    } else if (i > +hour) {
                        hours.push(i);
                    }
                }
                return hours;
            },
            disabledMinutes: () => {
                const minutes = [];
                for (let i = 0; i < 60; i++) {
                    if (startTime && startTimeMinute) {
                        if (i < startTimeMinute || i >= minute) {
                            minutes.push(i);
                        }
                    }
                    else if (i > +minute) {
                        minutes.push(i);
                    }
                }
                return minutes;
            },
        }
    }

    const {optionsMap} = useDemandOptions({
        keys: ['kol_platform'],
    })

    const columns: FormColumn[] = [
        {
            title: '#',
            dataIndex: 'index',
            width: 33,
            align: 'left',
            fixed: 'left',
            render: (text, record, index) => (<span
                style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: localPx2rem(14) }}
            >{index + 1}</span>)
        },
        {
            title: '平台',
            dataIndex: 'platform',
            width: 180,
            required: true,
            align: 'left',
            fixed: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'platform',
                    componentClass: 'select',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        options: optionsMap?.['kol_platform']?.options || []
                    }
                } as SFormItemProps
            }
        },
        {
            title: '作者ID',
            dataIndex: 'kol_code',
            width: 200,
            required: true,
            align: 'left',
            fixed: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'kol_code',
                    componentClass: 'input',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        ableCopy: true,
                    }
                } as SFormItemProps
            }
        },
        {
            title: '昵称',
            dataIndex: 'kol_name',
            width: 200,
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'kol_name',
                    componentClass: 'input',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small'
                    }
                } as SFormItemProps
            }
        },
        {
            title: '作品链接',
            dataIndex: 'work_url',
            width: 400,
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                const options = optionsMap?.['kol_platform']?.options || [];
                const platformItem = options.find(item => item?.value === showValue[index]?.platform);
                
                return {
                    formItemKey: 'work_url',
                    className: 'view-input-work_url',
                    componentClass: 'input',
                    view: view || record?.id,
                    required: typeof platformItem?.need_work_url === 'boolean' ? platformItem.need_work_url : true,
                    elementProps: {
                        size: 'small',
                        ableCopy: true,
                        textShowTitle: text || ''
                    },
                } as SFormItemProps
            }
        },
        {
            title: '加热费用',
            dataIndex: 'boost_cost',
            width: 264,
            required: true,
            align: 'right',
            formItem: (text, record, index) => {
                return {
                    className: `view-input-right ${!record?.id ? 'view-amount' : ''}`,
                    formItemKey: 'boost_cost',
                    componentClass: 'currencyType',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        selectStyle: { width: '80px' },
                        selectDisabled: true,
                    }
                } as SFormItemProps
            }
        },
        {
            title: '加热效果播放量',
            dataIndex: 'boost_view_count',
            width: 170,
            required: true,
            align: 'right',
            formItem: (text, record, index) => {
                return {
                    className: 'view-input-right',
                    formItemKey: 'boost_view_count',
                    componentClass: 'inputNumber',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small'
                    }
                } as SFormItemProps
            }
        },
        {
            title: '加热开始时间',
            dataIndex: 'boost_start_time',
            width: 200,
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                const newState = [...showValue]
                const endTime = newState?.[index]?.boost_end_time
                let days;
                if (endTime) {
                    days = day(endTime).format('YYYY-MM-DD')
                }
                return {
                    formItemKey: 'boost_start_time',
                    className: 'view-date-picker',
                    componentClass: 'datepicker',
                    view: view || record?.id,
                    elementProps: {
                        onChange: (date) => {
                            newState[index].boost_start_time = date
                            if (!date) {
                                newState[index].boost_end_time = null
                                setState(newState)
                            }
                        },
                        showNow: true,
                        disabledDate: (current) => {
                            if (!days) {
                                return current && current > day(days);
                            }
                            return current && day(current).valueOf() > day(days).valueOf()
                        },
                        // disabledTime: (current) => {
                        //     return disabledTime(current, record)
                        // },
                        showTime: { format: 'HH:mm' },
                        format: "YYYY-MM-DD HH:mm",
                        size: 'small'
                    },
                } as SFormItemProps
            }
        },
        {
            title: '加热结束时间',
            dataIndex: 'boost_end_time',
            width: 200,
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                const newState = [...showValue]
                const startTime = newState?.[index]?.boost_start_time
                let days;
                if (startTime) {
                    days = day(startTime).format('YYYY-MM-DD')
                }
                return {
                    formItemKey: 'boost_end_time',
                    className: 'view-date-picker',
                    componentClass: 'datepicker',
                    view: view || record?.id,
                    elementProps: {
                        onChange: (date) => {
                            newState[index].boost_end_time = date
                            if (!date) {
                                newState[index].boost_start_time = null
                                setState(newState)
                            }
                        },
                        disabledDate: (current) => {
                            if (!days) {
                                return current && current > day(days);
                            }
                            return current && (day(current).valueOf() < day(days).valueOf() || current > day())
                        },
                        // disabledTime: (current) => {
                        //     return disabledTime(current, record, startTime)
                        // },
                        showTime: { format: 'HH:mm' },
                        format: "YYYY-MM-DD HH:mm",
                        size: 'small'
                    },
                } as SFormItemProps
            }
        },
        {
            title: '加热链接上传时间',
            dataIndex: 'created_at',
            width: 200,
            align: 'left',
            render: (text, record, index) => {
                return (
                    <div>{text || "--"}</div>
                ) 
            }
        },
        {
            title: '加热链接上传用时',
            dataIndex: 'boost_upload_time',
            width: 170,
            align: 'left',
            fixed: 'right',
            render: (text, record, index) => {
                if (!record?.id) {
                    return '--'
                }
                // 上传时间
                const created_at = moment(record?.created_at).valueOf()
                // 加热结束时间
                const boost_end_time = moment(record?.boost_end_time).valueOf()
                const time = created_at - boost_end_time
                const value = calculateTimeConsuming(time);
                return (
                    <div>{value}{<OvertimeTag delay_grade={record?.delay_grade} delay_label={record.delay_label} className="marginLeft_4" tagCompColorLight />}</div>
                )
            }
        },
        !view && {
            title: '操作',
            dataIndex: 'operation',
            className: 'operation-cell',
            width: 61,
            align: 'left',
            fixed: 'right',
            render: (text, record, index) => {
                return <Button onClick={() => { handleDelete(record) }} type="text-subtle" danger icon={<Delete />} />
            }
        },
    ].filter(Boolean) as any

    const handleAddBoost = useMemoizedFn(() => {
        const newState = [...state]
        newState.push({
            key: Date.now(),
        })
        setState(newState)
    })


    const uploadFinishedCallback = useMemoizedFn((res) => {
        if (res?.data?.data?.length && res?.data?.fail_count === 0) {
            const newState = [...showValue]
            res.data.data?.forEach?.((item, index) => {
                newState.push({
                    ...item,
                    boost_cost: {
                        value: item?.boost_cost,
                    },
                    boost_start_time: item?.boost_start_time ? day(item?.boost_start_time).format('YYYY-MM-DD HH:mm') : '',
                    boost_end_time: item?.boost_end_time ? day(item?.boost_end_time).format('YYYY-MM-DD HH:mm') : '',
                    key: Date.now() + '' + index,
                })
            })
            setState(newState)
            closeImportFileModal()
        }
    })

    const {openImportFileModal, closeImportFileModal} = useImportFileModal({
        importType: ImportFileType.boost_effect,
        importIsMemory: false,
        uploadFinishedCallback: uploadFinishedCallback,
    })


    return (
        <div className={className}>
            <TableForm
                ref={tableFormRef}
                scroll={{ x: 'max-content', y: 360 }}
                className='boost-table-form'
                emptyText={<Empty imageNode={<NoFiles />} text="暂无数据" />}
                value={showValue}
                onChange={setState}
                formColumns={columns}
                summary={() => {
                    if (view) return null
                    return (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell align='left' fixed={'left'} index={0} colSpan={5} >
                                    <div style={{width: localPx2rem(1110)}}>
                                    <Space>
                                        <Button className='boost-table-container-add-button' size='medium' onClick={handleAddBoost} type='checked' icon={<Plus />}>新增</Button>
                                        <Button className='boost-table-container-add-button' size='medium' onClick={openImportFileModal} type='checked' icon={<NoticeTextLeft />}>批量导入</Button>
                                    </Space>
                                    </div>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align='left' index={3} colSpan={9} >
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )
                }}
            />
        </div>
    )
}

export default forwardRef(Index)           